import { useCallback, useMemo, useRef, useState } from 'react';

import { type RunFeedbackLabel } from '@apps/web/components/shared/run_feedback/run_feedback_prompt';
import { type TrainingNeedsReviewRunRead } from '@libraries/typescript/api_client';

type MutateTrainingNeedsReview = () => Promise<unknown>;

export function usePinnedTrainingRuns({
    runs,
    mutate,
    onFeedbackSaved,
}: {
    readonly runs: ReadonlyArray<TrainingNeedsReviewRunRead>;
    readonly mutate: MutateTrainingNeedsReview;
    readonly onFeedbackSaved?: () => void;
}) {
    // A row stays visible while its note flow is open even if polling or a
    // sibling save drops it from the latest payload. Keep the full object so a
    // now-missing pinned row can still render until the editor closes.
    const [reviewingRuns, setReviewingRuns] = useState<
        ReadonlyMap<string, TrainingNeedsReviewRunRead>
    >(() => new Map());
    const [reviewedRunIds, setReviewedRunIds] = useState<Set<string>>(
        () => new Set(),
    );

    const runsRef = useRef<ReadonlyArray<TrainingNeedsReviewRunRead>>([]);
    runsRef.current = runs;

    const handleReviewingChange = useCallback(
        (runId: string, isReviewing: boolean) => {
            setReviewingRuns((previous) => {
                const next = new Map(previous);
                if (isReviewing) {
                    const run = runsRef.current.find(
                        (candidate) => candidate.id === runId,
                    );
                    if (run) {
                        next.set(runId, run);
                    }
                } else {
                    next.delete(runId);
                }
                return next;
            });
            if (!isReviewing) {
                void mutate();
            }
        },
        [mutate],
    );

    const handleFeedbackSaved = useCallback(
        (runId: string, label: RunFeedbackLabel | null) => {
            setReviewedRunIds((current) => {
                const next = new Set(current);
                if (label === null) {
                    next.delete(runId);
                } else {
                    next.add(runId);
                }
                return next;
            });
            void mutate();
            onFeedbackSaved?.();
        },
        [mutate, onFeedbackSaved],
    );

    const visibleRuns = useMemo(() => {
        const filtered = runs.filter(
            (run) => reviewingRuns.has(run.id) || !reviewedRunIds.has(run.id),
        );
        const filteredIds = new Set(filtered.map((run) => run.id));
        const pinnedMissing = [...reviewingRuns.values()].filter(
            (run) => !filteredIds.has(run.id),
        );
        return [...filtered, ...pinnedMissing];
    }, [reviewedRunIds, reviewingRuns, runs]);

    return {
        reviewingRuns,
        visibleRuns,
        handleReviewingChange,
        handleFeedbackSaved,
    };
}
